// TOFU key pinning — docs/03-security.md §4.1–4.2.
//
// A friend's identity public key is learned at friendship time and pinned
// locally (trust on first use). Every call verifies SDP signatures against the
// PINNED key, not whatever the server currently claims. If the server-supplied
// key differs from the pin, the engine blocks call setup behind a
// "safety key changed" interlock until the user explicitly accepts the new key
// — same UX as Signal. A phrase-restore reuses the old key, so friends see no
// warning; only a re-registration trips it.

import type { KeyPinStorage } from '../types.js';

export type PinCheck =
  | { status: 'first-use' } // no pin yet — pin now, proceed
  | { status: 'pinned-ok' } // matches the pin — proceed
  | { status: 'changed'; pinnedKeyB64: string }; // MISMATCH — interlock

export class KeyPins {
  constructor(private readonly storage: KeyPinStorage) {}

  /** Compare a server-supplied key against the local pin for `userId`. */
  async check(userId: string, currentKeyB64: string): Promise<PinCheck> {
    const pinned = await this.storage.get(userId);
    if (pinned === null) return { status: 'first-use' };
    if (pinned === currentKeyB64) return { status: 'pinned-ok' };
    return { status: 'changed', pinnedKeyB64: pinned };
  }

  /**
   * Check and, on first use, pin. Returns the key that must be used for
   * signature verification: the pin when one exists, else the new key (now
   * pinned). On 'changed' the caller must NOT verify against either key —
   * it must raise the interlock.
   */
  async checkAndPin(userId: string, currentKeyB64: string): Promise<PinCheck> {
    const result = await this.check(userId, currentKeyB64);
    if (result.status === 'first-use') {
      await this.storage.set(userId, currentKeyB64);
    }
    return result;
  }

  /** User explicitly accepted a key change: replace the pin. */
  async acceptChange(userId: string, newKeyB64: string): Promise<void> {
    await this.storage.set(userId, newKeyB64);
  }

  /** Unfriend/block: forget the pin. */
  async forget(userId: string): Promise<void> {
    await this.storage.delete(userId);
  }

  async pinnedKey(userId: string): Promise<string | null> {
    return this.storage.get(userId);
  }
}

/** In-memory pin storage for tests and pre-storage boot. */
export class MemoryPinStorage implements KeyPinStorage {
  private readonly map = new Map<string, string>();

  get(userId: string): Promise<string | null> {
    return Promise.resolve(this.map.get(userId) ?? null);
  }

  set(userId: string, publicKeyB64: string): Promise<void> {
    this.map.set(userId, publicKeyB64);
    return Promise.resolve();
  }

  delete(userId: string): Promise<void> {
    this.map.delete(userId);
    return Promise.resolve();
  }
}
